import type { AttachedDoc, Class, Doc, Hyperlink, IntegrationKind, Mixin, Ref, Timestamp } from '@hcengineering/core'
import type { Asset, IntlString, Metadata, Plugin, Resource } from '@hcengineering/platform'
import { plugin } from '@hcengineering/platform'
import type { IntegrationType } from '@hcengineering/setting'
import type { Issue, IssueStatus, Project } from '@hcengineering/tracker'
import type { AnyComponent } from '@hcengineering/ui'
import type { Action, ActionCategory, ViewAction, Viewlet } from '@hcengineering/view'

/**
 * @public
 */
export const gitlabId = 'dztek-gitlab' as Plugin

/**
 * @public
 */
export const gitlabIntegrationKind = 'dztek-gitlab' as IntegrationKind

/**
 * @public
 *
 * State of a GitLab merge request as reported by the GitLab REST API.
 */
export type MergeRequestState = 'opened' | 'closed' | 'locked' | 'merged'

/**
 * @public
 *
 * Mixin on a tracker project that maps it to one GitLab project.
 */
export interface GitLabProject extends Project {
  gitlabProjectId: number
  pathWithNamespace: string
  webUrl: Hyperlink
  syncEnabled: boolean
  lastSyncAt: Timestamp | null
  /** Huly status assigned to GitLab issues in state `opened`; the project default status when null. */
  openedStatus: Ref<IssueStatus> | null
  /** Huly status assigned to GitLab issues in state `closed`; the first status of the Won category when null. */
  closedStatus: Ref<IssueStatus> | null
}

/**
 * @public
 *
 * Mixin on a tracker issue that mirrors one GitLab issue.
 */
export interface GitLabIssue extends Issue {
  /** Project-scoped GitLab issue number. */
  iid: number
  /** Instance-wide GitLab issue id, the idempotency key of the mirror. */
  gitlabId: number
  webUrl: Hyperlink
  /** Time of the last write by the gitlab service. */
  syncedAt: Timestamp
  /** GitLab `updated_at` of the last synchronized revision. */
  lastEtag: string
  /** True once GitLab answers 404 for the issue; the service stops pushing changes to it. */
  deleted: boolean
  mergeRequests: number
}

/**
 * @public
 *
 * A GitLab merge request that references a mirrored issue.
 */
export interface MergeRequest extends AttachedDoc {
  attachedTo: Ref<Issue>
  iid: number
  gitlabId: number
  title: string
  state: MergeRequestState
  webUrl: Hyperlink
  author: string
  sourceBranch: string
  targetBranch: string
  updatedAt: Timestamp
}

export default plugin(gitlabId, {
  class: {
    MergeRequest: '' as Ref<Class<MergeRequest>>
  },
  mixin: {
    GitLabProject: '' as Ref<Mixin<GitLabProject>>,
    GitLabIssue: '' as Ref<Mixin<GitLabIssue>>
  },
  component: {
    Connect: '' as AnyComponent,
    Configure: '' as AnyComponent,
    ServiceStatus: '' as AnyComponent,
    IconGitlab: '' as AnyComponent,
    MergeRequestPresenter: '' as AnyComponent,
    MergeRequests: '' as AnyComponent,
    MergeRequestStatePresenter: '' as AnyComponent,
    GitLabIssueHeader: '' as AnyComponent,
    SyncButton: '' as AnyComponent
  },
  action: {
    OpenIssueInGitLab: '' as Ref<Action>,
    OpenProjectInGitLab: '' as Ref<Action>,
    SyncIssueNow: '' as Ref<Action>,
    SyncProjectNow: '' as Ref<Action>
  },
  actionImpl: {
    OpenInGitLab: '' as ViewAction,
    SyncNow: '' as ViewAction
  },
  function: {
    IsMirroredIssue: '' as Resource<(doc?: Doc | Doc[]) => Promise<boolean>>,
    IsMappedProject: '' as Resource<(doc?: Doc | Doc[]) => Promise<boolean>>
  },
  category: {
    GitLab: '' as Ref<ActionCategory>
  },
  viewlet: {
    MergeRequests: '' as Ref<Viewlet>
  },
  integrationType: {
    Gitlab: '' as Ref<IntegrationType>
  },
  metadata: {
    ServiceUrl: '' as Metadata<string>
  },
  string: {
    Gitlab: '' as IntlString,
    Description: '' as IntlString,
    GitLabProject: '' as IntlString,
    GitLabIssue: '' as IntlString,
    MergeRequest: '' as IntlString,
    MergeRequests: '' as IntlString,
    OpenInGitLab: '' as IntlString,
    SyncNow: '' as IntlString,
    OpenedStatus: '' as IntlString,
    ClosedStatus: '' as IntlString,
    LastSyncAt: '' as IntlString
  },
  icon: {
    Gitlab: '' as Asset,
    MergeRequest: '' as Asset
  }
})
